import React, { useState, useEffect, useCallback } from 'react'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Building2,
  Loader2,
  Edit2,
  Save,
  X,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react'
import PageHeader from '@/components/PageHeader'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const NUM_COLS = [
  { key: 'lean',         label: 'Lean (%)',          start: 'lean_start',         end: 'lean_end'         },
  { key: 'ideal',        label: 'Ideal (%)',          start: 'ideal_start',        end: 'ideal_end'        },
  { key: 'average',      label: 'Average (%)',        start: 'average_start',      end: 'average_end'      },
  { key: 'aboveaverage', label: 'Above Avg (%)',      start: 'aboveaverage_start', end: 'aboveaverage_end' },
]

const EXTRA_COLS = [
  { key: 'bf_can_lose_per_week', label: 'BF Loss / Wk' },
  { key: 'mbr_gain_per_pound',   label: 'MBR Gain / lb' },
]

const GENDER_LABELS = { m: 'Male', f: 'Female' }

// ─── Sub-components ──────────────────────────────────────────────────────────

function ComplianceCard({ color, label, fields, editing, values, onChange }) {
  const palette = {
    yellow: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      dot: 'bg-amber-400',
      text: 'text-amber-700',
      badge: 'bg-amber-100 text-amber-800',
    },
    green: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      dot: 'bg-emerald-500',
      text: 'text-emerald-700',
      badge: 'bg-emerald-100 text-emerald-800',
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      dot: 'bg-red-500',
      text: 'text-red-700',
      badge: 'bg-red-100 text-red-800',
    },
  }[color]

  return (
    <div className={`rounded-lg border ${palette.border} ${palette.bg} p-5`}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`h-3 w-3 rounded-full ${palette.dot}`} />
        <span className={`text-sm font-semibold ${palette.text}`}>{label}</span>
      </div>
      <div className="space-y-3">
        {fields.map(({ key, label: fLabel }) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <span className="text-xs text-slate-500 w-20">{fLabel}</span>
            {editing ? (
              <Input
                type="number"
                className="h-8 w-28 text-sm rounded-xl px-3"
                value={values[key] ?? ''}
                onChange={e => onChange(key, e.target.value)}
              />
            ) : (
              <span className={`text-sm font-semibold px-3 py-1 rounded-xl ${palette.badge}`}>
                {values[key] ?? '—'}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function RangeCell({ row, startKey, endKey, editing, onChange }) {
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          type="number"
          step="0.1"
          className="h-7 w-16 text-xs rounded-lg px-2"
          value={row[startKey] ?? ''}
          onChange={e => onChange(row.id, startKey, e.target.value)}
        />
        <span className="text-slate-400 text-xs">–</span>
        <Input
          type="number"
          step="0.1"
          className="h-7 w-16 text-xs rounded-lg px-2"
          value={row[endKey] ?? ''}
          onChange={e => onChange(row.id, endKey, e.target.value)}
        />
      </div>
    )
  }
  return (
    <span className="text-sm text-slate-700 tabular-nums">
      {row[startKey]} – {row[endKey]}
    </span>
  )
}

function ExtraCell({ row, fieldKey, editing, onChange }) {
  if (editing) {
    return (
      <Input
        type="number"
        step="0.01"
        className="h-7 w-20 text-xs rounded-lg px-2"
        value={row[fieldKey] ?? ''}
        onChange={e => onChange(row.id, fieldKey, e.target.value)}
      />
    )
  }
  return <span className="text-sm text-slate-700 tabular-nums">{row[fieldKey]}</span>
}

function AgeRangeTable({ rows, editing, onCellChange }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm border-collapse min-w-[900px]">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
              Age Range
            </th>
            {NUM_COLS.map(c => (
              <th
                key={c.key}
                className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
              >
                {c.label}
              </th>
            ))}
            {EXTRA_COLS.map(c => (
              <th
                key={c.key}
                className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={row.id}
              className={`border-b border-slate-100 last:border-0 transition-colors ${
                idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
              } hover:bg-fitness-primary/5`}
            >
              <td className="px-4 py-3 whitespace-nowrap">
                {editing ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      className="h-7 w-14 text-xs rounded-lg px-2"
                      value={row.start_age ?? ''}
                      onChange={e => onCellChange(row.id, 'start_age', e.target.value)}
                    />
                    <span className="text-slate-400 text-xs">–</span>
                    <Input
                      type="number"
                      className="h-7 w-14 text-xs rounded-lg px-2"
                      value={row.end_age ?? ''}
                      onChange={e => onCellChange(row.id, 'end_age', e.target.value)}
                    />
                  </div>
                ) : (
                  <span className="font-medium text-slate-800 tabular-nums">
                    {row.start_age} – {row.end_age}
                  </span>
                )}
              </td>
              {NUM_COLS.map(c => (
                <td key={c.key} className="px-4 py-3 whitespace-nowrap">
                  <RangeCell
                    row={row}
                    startKey={c.start}
                    endKey={c.end}
                    editing={editing}
                    onChange={onCellChange}
                  />
                </td>
              ))}
              {EXTRA_COLS.map(c => (
                <td key={c.key} className="px-4 py-3 whitespace-nowrap">
                  <ExtraCell
                    row={row}
                    fieldKey={c.key}
                    editing={editing}
                    onChange={onCellChange}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const SiteSettings = () => {
  const { companyUrl } = useAuthStore()

  const [companies, setCompanies] = useState([])
  const [selectedCompanyId, setSelectedCompanyId] = useState(null)
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [companiesError, setCompaniesError] = useState(null)

  const [settings, setSettings] = useState(null)
  const [draft, setDraft] = useState(null)
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [settingsError, setSettingsError] = useState(null)

  const [editing, setEditing] = useState(false)
  const [genderTab, setGenderTab] = useState('m')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  // ── Fetch companies ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true)
      setCompaniesError(null)
      const result = await apiService.get(
        apiService.withCompanyEndpoint('/companyadmin/getcompanies', companyUrl)
      )
      if (result.success && Array.isArray(result.data)) {
        setCompanies(result.data)
        if (result.data.length > 0) {
          setSelectedCompanyId(result.data[0].id)
        }
      } else {
        setCompaniesError(result.error || 'Failed to load companies.')
      }
      setLoadingCompanies(false)
    }
    fetchCompanies()
  }, [companyUrl])

  // ── Fetch compliance settings when company changes ───────────────────────
  useEffect(() => {
    if (!selectedCompanyId) return
    const fetchSettings = async () => {
      setLoadingSettings(true)
      setSettingsError(null)
      setSettings(null)
      setDraft(null)
      setEditing(false)
      setSaveSuccess(false)
      const result = await apiService.post(
        apiService.withCompanyEndpoint('/superadmin/getcompliancesettings', companyUrl),
        { company_id: selectedCompanyId }
      )
      if (result.success && result.data) {
        setSettings(result.data)
        setDraft(JSON.parse(JSON.stringify(result.data)))
      } else {
        setSettingsError(result.error || 'Failed to load compliance settings.')
      }
      setLoadingSettings(false)
    }
    fetchSettings()
  }, [selectedCompanyId, companyUrl])

  // ── Draft helpers ────────────────────────────────────────────────────────
  const handleTopLevelChange = useCallback((key, value) => {
    setDraft(prev => ({ ...prev, [key]: value === '' ? '' : Number(value) }))
  }, [])

  const handleCellChange = useCallback((rowId, field, value) => {
    setDraft(prev => ({
      ...prev,
      ageranges: prev.ageranges.map(r =>
        r.id === rowId ? { ...r, [field]: value === '' ? '' : Number(value) } : r
      ),
    }))
  }, [])

  const handleCancelEdit = () => {
    setDraft(JSON.parse(JSON.stringify(settings)))
    setEditing(false)
    setSaveError(null)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)

    const result = await apiService.updateComplianceSettings(companyUrl, draft.id, {
      colorcompliance_yellow_min: Number(draft.colorcompliance_yellow_min),
      colorcompliance_yellow_max: Number(draft.colorcompliance_yellow_max),
      colorcompliance_green_min: Number(draft.colorcompliance_green_min),
      colorcompliance_green_max: Number(draft.colorcompliance_green_max),
      colorcompliance_red_min: Number(draft.colorcompliance_red_min),
    })

    if (!result.success) {
      setSaveError(result.error || 'Failed to save compliance settings.')
      setSaving(false)
      return
    }

    setSettings(JSON.parse(JSON.stringify(draft)))
    setEditing(false)
    setSaving(false)
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 3000)
  }

  // ── Rebuild indices ──────────────────────────────────────────────────────
  const [showReindexDialog, setShowReindexDialog] = useState(false)
  const [reindexSaving, setReindexSaving] = useState(false)
  const [reindexError, setReindexError] = useState(null)
  const [reindexSuccess, setReindexSuccess] = useState(false)

  const handleOpenReindexDialog = () => {
    setReindexError(null)
    setShowReindexDialog(true)
  }

  const handleConfirmReindex = async () => {
    if (!selectedCompanyId) return
    setReindexSaving(true)
    setReindexError(null)

    const result = await apiService.reindexCompanyData(companyUrl, selectedCompanyId)

    if (!result.success) {
      setReindexError(result.error || 'Failed to rebuild indices.')
      setReindexSaving(false)
      return
    }

    setReindexSaving(false)
    setShowReindexDialog(false)
    setReindexSuccess(true)
    setTimeout(() => setReindexSuccess(false), 4000)
  }

  // ── Derived data ─────────────────────────────────────────────────────────
  const maleRows = draft?.ageranges?.filter(r => r.gender === 'm') ?? []
  const femaleRows = draft?.ageranges?.filter(r => r.gender === 'f') ?? []

  const complianceFields = draft
    ? [
        {
          color: 'yellow',
          label: 'Yellow Range',
          fields: [
            { key: 'colorcompliance_yellow_min', label: 'Min' },
            { key: 'colorcompliance_yellow_max', label: 'Max' },
          ],
        },
        {
          color: 'green',
          label: 'Green Range',
          fields: [
            { key: 'colorcompliance_green_min', label: 'Min' },
            { key: 'colorcompliance_green_max', label: 'Max' },
          ],
        },
        {
          color: 'red',
          label: 'Red Range',
          fields: [{ key: 'colorcompliance_red_min', label: 'Min (start)' }],
        },
      ]
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        backTo="/platform-setup"
        backLabel="Platform setup"
        title="Site settings"
        subtitle="Configure compliance thresholds and age-range parameters per company"
      />

      {/* Company Selector */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-fitness-primary/5 rounded-xl">
              <Building2 className="h-5 w-5 text-fitness-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">Select Company</p>
              <p className="text-xs text-slate-500">Settings are loaded per company</p>
            </div>
          </div>

          {loadingCompanies ? (
            <div className="flex items-center gap-2 py-2 text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading companies…</span>
            </div>
          ) : companiesError ? (
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{companiesError}</span>
            </div>
          ) : (
            <Select
              value={selectedCompanyId ? String(selectedCompanyId) : ''}
              onValueChange={val => setSelectedCompanyId(Number(val))}
            >
              <SelectTrigger className="max-w-sm">
                <SelectValue placeholder="Choose a company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardContent>
      </Card>

      {/* Maintenance */}
      {selectedCompanyId && (
        <Card className="shadow-sm">
          <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-slate-800">Search Indices</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Rebuild the search indices for this company's templates and content.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {reindexSuccess && (
                <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium animate-fade-in">
                  <CheckCircle2 className="h-4 w-4" />
                  Index rebuild initiated
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenReindexDialog}
                className="rounded-xl gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                Rebuild Indices
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Content */}
      {loadingSettings && (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
          <Loader2 className="h-8 w-8 animate-spin text-fitness-primary" />
          <p className="text-sm">Loading compliance settings…</p>
        </div>
      )}

      {settingsError && !loadingSettings && (
        <div className="flex items-center gap-3 p-5 rounded-lg bg-red-50 border border-red-200 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm">{settingsError}</p>
        </div>
      )}

      {draft && !loadingSettings && (
        <>
          {/* Section header row */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Compliance Settings</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Company ID {draft.company_id}
                {draft.updated_at && (
                  <> · Last updated {new Date(draft.updated_at).toLocaleDateString()}</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {saveError && (
                <span className="flex items-center gap-1.5 text-sm text-red-600 font-medium">
                  <AlertCircle className="h-4 w-4" />
                  {saveError}
                </span>
              )}
              {saveSuccess && (
                <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                  <CheckCircle2 className="h-4 w-4" />
                  Saved
                </span>
              )}
              {editing ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="rounded-xl gap-1.5"
                  >
                    <X className="h-4 w-4" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-xl gap-1.5 bg-fitness-primary hover:bg-fitness-primary/90"
                  >
                    {saving ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                    ) : (
                      <><Save className="h-4 w-4" /> Save Changes</>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSaveError(null); setEditing(true) }}
                  className="rounded-xl gap-1.5"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit Settings
                </Button>
              )}
            </div>
          </div>

          {/* Color Compliance Thresholds */}
          <Card className="shadow-sm">
            <CardHeader className="px-6 pt-6 pb-2">
              <CardTitle className="text-base font-semibold text-slate-800">
                Color Compliance Thresholds
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Define the numeric ranges that map to each compliance color indicator.
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {complianceFields.map(({ color, label, fields }) => (
                  <ComplianceCard
                    key={color}
                    color={color}
                    label={label}
                    fields={fields}
                    editing={editing}
                    values={draft}
                    onChange={handleTopLevelChange}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Age Ranges */}
          <Card className="shadow-sm">
            <CardHeader className="px-6 pt-6 pb-2">
              <CardTitle className="text-base font-semibold text-slate-800">
                Body Fat Age Ranges
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Configure body-fat category boundaries, weekly loss targets, and MBR multipliers by age group.
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-3">
              {/* Gender tabs */}
              <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit mb-5">
                {Object.entries(GENDER_LABELS).map(([g, label]) => (
                  <button
                    key={g}
                    onClick={() => setGenderTab(g)}
                    className={`px-5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      genderTab === g
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <AgeRangeTable
                rows={genderTab === 'm' ? maleRows : femaleRows}
                editing={editing}
                onCellChange={handleCellChange}
              />

              {(genderTab === 'm' ? maleRows : femaleRows).length === 0 && (
                <div className="text-center py-10 text-slate-400 text-sm">
                  No age ranges found for this gender.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Rebuild Indices Confirmation Dialog ── */}
      <Dialog open={showReindexDialog} onOpenChange={(open) => { if (!reindexSaving) setShowReindexDialog(open) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-fitness-primary/10 rounded-xl">
                <RefreshCw className="h-5 w-5 text-fitness-primary" />
              </div>
              <DialogTitle className="text-lg font-semibold text-slate-800">
                Rebuild Indices
              </DialogTitle>
            </div>
            <DialogDescription className="text-sm text-slate-500 leading-relaxed pt-1">
              Initiate index rebuild. Process will begin immediately and will complete shortly.
            </DialogDescription>
          </DialogHeader>

          {reindexError && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{reindexError}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              variant="outline"
              className="rounded-xl flex-1 sm:flex-none"
              onClick={() => setShowReindexDialog(false)}
              disabled={reindexSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmReindex}
              disabled={reindexSaving}
              className="rounded-xl flex-1 sm:flex-none bg-fitness-primary hover:bg-fitness-primary/90 text-primary-foreground gap-1.5"
            >
              {reindexSaving ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Initiating…</>
              ) : (
                <><RefreshCw className="h-4 w-4" /> Rebuild Indices</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SiteSettings
